import * as fabric from 'fabric';
import { Stroke, Point } from '../../types';
import { getPointsToSmoothPath } from '../../utils/drawing';
import { gaussianSmooth, createCatmullRomPath } from '../../utils/smoothPath';

// Use the EXACT same shape generation function from recording
const generateShapePoints = (tool: string, startPoint: Point, endPoint: Point): Point[] => {
  const points: Point[] = [];
  const timestamp = Date.now();
  
  switch (tool) {
    case 'rectangle':
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      points.push({ x: endPoint.x, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ ...endPoint, timestamp: endPoint.timestamp || timestamp });
      points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      break;
      
    case 'parallelogram':
      const paraOffset = Math.abs(endPoint.x - startPoint.x) * 0.2;
      points.push({ x: startPoint.x + paraOffset, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x - paraOffset, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + paraOffset, y: startPoint.y, pressure: 0.5, timestamp });
      break;
      
    case 'trapezoid':
      const trapOffset = Math.abs(endPoint.x - startPoint.x) * 0.15;
      points.push({ x: startPoint.x + trapOffset, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x - trapOffset, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + trapOffset, y: startPoint.y, pressure: 0.5, timestamp });
      break;
    
    case 'circle':
    case 'ellipse':
      const centerX = (startPoint.x + endPoint.x) / 2;
      const centerY = (startPoint.y + endPoint.y) / 2;
      const radiusX = Math.abs(endPoint.x - startPoint.x) / 2;
      const radiusY = Math.abs(endPoint.y - startPoint.y) / 2;
      const steps = 72;
      
      for (let i = 0; i <= steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        points.push({
          x: centerX + radiusX * Math.cos(angle),
          y: centerY + radiusY * Math.sin(angle),
          pressure: 0.5,
          timestamp
        });
      }
      break;
    
    case 'line':
    case 'arrow':
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      points.push({ ...endPoint, timestamp: endPoint.timestamp || timestamp });
      break;
    
    case 'triangle':
      const midX = (startPoint.x + endPoint.x) / 2;
      // Use the actual drag rectangle dimensions
      const width = Math.abs(endPoint.x - startPoint.x);
      const height = Math.abs(endPoint.y - startPoint.y);
      
      // If height is very small (horizontal drag), create an equilateral triangle
      const triangleHeight = height < 5 ? width * Math.sqrt(3) / 2 : height;
      
      // Determine the top and bottom based on drag direction
      let topY, bottomY;
      if (height < 5) {
        // Horizontal drag - create triangle pointing up from the line
        topY = Math.min(startPoint.y, endPoint.y) - triangleHeight;
        bottomY = Math.max(startPoint.y, endPoint.y);
      } else {
        // Normal drag - use actual positions
        topY = Math.min(startPoint.y, endPoint.y);
        bottomY = Math.max(startPoint.y, endPoint.y);
      }
      
      
      points.push({ x: midX, y: topY, pressure: 0.5, timestamp });
      points.push({ x: Math.min(startPoint.x, endPoint.x), y: bottomY, pressure: 0.5, timestamp });
      points.push({ x: Math.max(startPoint.x, endPoint.x), y: bottomY, pressure: 0.5, timestamp });
      points.push({ x: midX, y: topY, pressure: 0.5, timestamp });
      break;
    
    case 'star':
      const cx = (startPoint.x + endPoint.x) / 2;
      const cy = (startPoint.y + endPoint.y) / 2;
      const outerRadius = Math.min(Math.abs(endPoint.x - startPoint.x), Math.abs(endPoint.y - startPoint.y)) / 2;
      const innerRadius = outerRadius * 0.4;
      const numPoints = 5;
      
      for (let i = 0; i <= numPoints * 2; i++) {
        const angle = (i / (numPoints * 2)) * Math.PI * 2 - Math.PI / 2;
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        points.push({
          x: cx + radius * Math.cos(angle),
          y: cy + radius * Math.sin(angle),
          pressure: 0.5,
          timestamp
        });
      }
      break;
    
    case 'hexagon':
    case 'pentagon':
      const polygonCenter = { x: (startPoint.x + endPoint.x) / 2, y: (startPoint.y + endPoint.y) / 2 };
      const polygonRadius = Math.min(Math.abs(endPoint.x - startPoint.x), Math.abs(endPoint.y - startPoint.y)) / 2;
      const sides = tool === 'hexagon' ? 6 : 5;
      
      for (let i = 0; i <= sides; i++) {
        const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
        points.push({
          x: polygonCenter.x + polygonRadius * Math.cos(angle),
          y: polygonCenter.y + polygonRadius * Math.sin(angle),
          pressure: 0.5,
          timestamp
        });
      }
      break;
    
    case 'diamond':
      const diamondCx = (startPoint.x + endPoint.x) / 2;
      const diamondCy = (startPoint.y + endPoint.y) / 2;
      points.push({ x: diamondCx, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: diamondCy, pressure: 0.5, timestamp });
      points.push({ x: diamondCx, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x, y: diamondCy, pressure: 0.5, timestamp });
      points.push({ x: diamondCx, y: startPoint.y, pressure: 0.5, timestamp });
      break;
    
    case 'cube':
      const cubeSize = Math.min(Math.abs(endPoint.x - startPoint.x), Math.abs(endPoint.y - startPoint.y));
      const offset = cubeSize * 0.3;
      
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      points.push({ x: startPoint.x + cubeSize, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      
      points.push({ x: startPoint.x + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y + cubeSize - offset, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
      
      points.push({ x: startPoint.x + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      points.push({ x: startPoint.x, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + offset, y: startPoint.y + cubeSize - offset, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y + cubeSize - offset, pressure: 0.5, timestamp });
      break;
    
    case 'cylinder':
      const cylCenterX = (startPoint.x + endPoint.x) / 2;
      const cylRadiusX = Math.abs(endPoint.x - startPoint.x) / 2;
      const cylRadiusY = cylRadiusX * 0.25;
      const cylSegments = 24;
      
      for (let i = 0; i <= cylSegments; i++) {
        const angle = (Math.PI * 2 * i) / cylSegments;
        points.push({
          x: cylCenterX + cylRadiusX * Math.cos(angle),
          y: startPoint.y + cylRadiusY * Math.sin(angle),
          pressure: 0.5,
          timestamp
        });
      }
      
      points.push({ x: cylCenterX + cylRadiusX, y: endPoint.y, pressure: 0.5, timestamp });
      
      for (let i = 0; i <= cylSegments / 2; i++) {
        const angle = (Math.PI * i) / (cylSegments / 2);
        points.push({
          x: cylCenterX + cylRadiusX * Math.cos(angle),
          y: endPoint.y + cylRadiusY * Math.sin(angle),
          pressure: 0.5,
          timestamp
        });
      }
      
      points.push({ x: cylCenterX - cylRadiusX, y: startPoint.y, pressure: 0.5, timestamp });
      break;
    
    case 'pyramid':
      const pyrBase = Math.abs(endPoint.x - startPoint.x);
      const pyrHeight = Math.abs(endPoint.y - startPoint.y);
      const pyrCenterX = (startPoint.x + endPoint.x) / 2;
      
      
      points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x - pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      
      points.push({ x: pyrCenterX, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
      points.push({ x: pyrCenterX, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x - pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
      points.push({ x: pyrCenterX, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: startPoint.x + pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
      
      break;
    
    case 'grid':
      const gridColCount = 8;
      const gridRowCount = 8;
      const gridCellWidth = (endPoint.x - startPoint.x) / gridColCount;
      const gridCellHeight = (endPoint.y - startPoint.y) / gridRowCount;
      
      for (let col = 0; col <= gridColCount; col++) {
        const x = startPoint.x + col * gridCellWidth;
        if (col > 0) {
          points.push({ x, y: startPoint.y, pressure: 0, timestamp });
        }
        points.push({ x, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x, y: endPoint.y, pressure: 0.5, timestamp });
      }
      
      for (let row = 0; row <= gridRowCount; row++) {
        const y = startPoint.y + row * gridCellHeight;
        points.push({ x: startPoint.x, y, pressure: 0, timestamp });
        points.push({ x: startPoint.x, y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y, pressure: 0.5, timestamp });
      }
      break;
    
    case 'coordinate':
      const coordCenterX = (startPoint.x + endPoint.x) / 2;
      const coordCenterY = (startPoint.y + endPoint.y) / 2;
      
      points.push({ x: startPoint.x, y: coordCenterY, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: coordCenterY, pressure: 0.5, timestamp });
      
      points.push({ x: endPoint.x - 10, y: coordCenterY - 5, pressure: 0, timestamp });
      points.push({ x: endPoint.x - 10, y: coordCenterY - 5, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x, y: coordCenterY, pressure: 0.5, timestamp });
      points.push({ x: endPoint.x - 10, y: coordCenterY + 5, pressure: 0.5, timestamp });
      
      points.push({ x: coordCenterX, y: startPoint.y, pressure: 0, timestamp });
      points.push({ x: coordCenterX, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: coordCenterX, y: endPoint.y, pressure: 0.5, timestamp });
      
      points.push({ x: coordCenterX - 5, y: startPoint.y + 10, pressure: 0, timestamp });
      points.push({ x: coordCenterX - 5, y: startPoint.y + 10, pressure: 0.5, timestamp });
      points.push({ x: coordCenterX, y: startPoint.y, pressure: 0.5, timestamp });
      points.push({ x: coordCenterX + 5, y: startPoint.y + 10, pressure: 0.5, timestamp });
      break;
    
    case 'coordinateGrid':
      const cgCenterX = (startPoint.x + endPoint.x) / 2;
      const cgCenterY = (startPoint.y + endPoint.y) / 2;
      const cgSpacing = 30;
      
      const startCol = Math.floor(startPoint.x / cgSpacing) * cgSpacing;
      const endCol = Math.ceil(endPoint.x / cgSpacing) * cgSpacing;
      
      for (let x = startCol; x <= endCol; x += cgSpacing) {
        if (x >= startPoint.x && x <= endPoint.x) {
          if (x !== startCol) points.push({ x, y: startPoint.y, pressure: 0, timestamp });
          points.push({ x, y: startPoint.y, pressure: -0.2, timestamp });
          points.push({ x, y: endPoint.y, pressure: -0.2, timestamp });
        }
      }
      
      const startRow = Math.floor(startPoint.y / cgSpacing) * cgSpacing;
      const endRow = Math.ceil(endPoint.y / cgSpacing) * cgSpacing;
      
      for (let y = startRow; y <= endRow; y += cgSpacing) {
        if (y >= startPoint.y && y <= endRow) {
          points.push({ x: startPoint.x, y, pressure: 0, timestamp });
          points.push({ x: startPoint.x, y, pressure: -0.2, timestamp });
          points.push({ x: endPoint.x, y, pressure: -0.2, timestamp });
        }
      }
      break;
    
    default:
      points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
      points.push({ ...endPoint, timestamp: endPoint.timestamp || timestamp });
      break;
  }
  
  return points;
};

// Interpolate points to simulate coalesced events (missing high-frequency input)
const interpolatePoints = (points: Point[]): Point[] => {
  if (points.length < 2) return points;
  
  const interpolated: Point[] = [points[0]];
  
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    
    // Calculate distance between points
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // If points are far apart, add interpolated points (simulate missing coalesced events)
    if (distance > 4) { // Threshold for interpolation
      const steps = Math.min(Math.ceil(distance / 3), 5); // Max 5 interpolated points
      
      for (let j = 1; j < steps; j++) {
        const t = j / steps;
        
        interpolated.push({
          x: prev.x + dx * t,
          y: prev.y + dy * t,
          pressure: prev.pressure !== undefined && curr.pressure !== undefined 
            ? prev.pressure + (curr.pressure - prev.pressure) * t 
            : curr.pressure || prev.pressure,
          timestamp: prev.timestamp !== undefined && curr.timestamp !== undefined
            ? prev.timestamp + (curr.timestamp - prev.timestamp) * t
            : curr.timestamp || prev.timestamp
        });
      }
    }
    
    interpolated.push(curr);
  }
  
  return interpolated;
};

// Use the EXACT same path string generation from recording
const getShapePathString = (points: Point[], tool?: string): string => {
  if (points.length < 2) return '';
  
  // Special handling for grid and coordinate - use pen up/down based on pressure
  if (tool === 'grid' || tool === 'coordinate' || tool === 'coordinateGrid') {
    return points.map((point, index) => {
      // Use M (move) for pen up (pressure 0) or first point
      if (index === 0 || point.pressure === 0) {
        return `M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
      }
      return `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    }).join(' ');
  }
  
  // Use straight lines for other shapes
  const pathData = points.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
  ).join(' ');
  
  // Close path for closed shapes (not for line, arrow, coordinate, grid)
  const openShapes = ['line', 'arrow', 'coordinate', 'grid', 'coordinateGrid'];
  if (tool && !openShapes.includes(tool)) {
    return pathData + ' Z';
  }
  
  return pathData;
};

// Create fabric object from stroke
export const createFabricObject = (stroke: Stroke): fabric.Object | null => {
  if (!stroke || !stroke.points || stroke.points.length === 0) {
    console.error('Invalid stroke data:', stroke);
    return null;
  }

  const points = stroke.points;
  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];
  
  if (!firstPoint || !lastPoint) {
    console.error('Invalid points in stroke:', points);
    return null;
  }

  const commonProps = {
    stroke: stroke.color,
    strokeWidth: stroke.width,
    fill: '',
    strokeLineCap: 'round' as const,
    strokeLineJoin: 'round' as const,
    selectable: false,
    evented: false,
    strokeId: stroke.id,
  };

  let fabricObject: fabric.Object | null = null;


  switch (stroke.tool) {
    case 'eraser':
      // Eraser doesn't create fabric objects
      return null;

    case 'rectangle':
    case 'triangle':
    case 'star':
    case 'hexagon':
    case 'pentagon':
    case 'diamond':
    case 'parallelogram':
    case 'trapezoid':
    case 'cube':
    case 'pyramid':
    case 'cylinder':
    case 'coordinate':
    case 'grid':
    case 'coordinateGrid' as any:
    case 'line':
    case 'arrow':
    case 'circle':
    case 'ellipse':
      // For ALL shapes, use the EXACT same system as recording
      let finalPoints = points;
      
      // ALWAYS regenerate points for shapes to ensure consistency
      finalPoints = generateShapePoints(stroke.tool, firstPoint, lastPoint);
      
      // Use the exact same path generation as in recording
      const pathData = getShapePathString(finalPoints, stroke.tool);
      
      
      if (pathData && pathData.trim() !== '') {
        fabricObject = new fabric.Path(pathData, {
          ...commonProps,
          strokeDashArray: undefined // No dashed line for final shape - SAME AS RECORDING
        });
        
        // Get the path bounds before any adjustments
        const pathBounds = (fabricObject as fabric.Path).getBoundingRect();
        
        
        // For debugging: Check if position needs adjustment
        const expectedLeft = Math.min(...finalPoints.map(p => p.x));
        const expectedTop = Math.min(...finalPoints.map(p => p.y));
        
      }
      break;

    case 'pen':
    case 'pencil':
      // Apply the SAME advanced smoothing as whiteboard
      // 0. For pen, interpolate points first to simulate coalesced events
      let processedPoints = points;
      if (stroke.tool === 'pen') {
        processedPoints = interpolatePoints(points);
      }
      
      // 1. Apply Gaussian smoothing first (sigma = 1.5)
      const smoothedPoints = gaussianSmooth(processedPoints, 1.5);
      // 2. Create smooth path using Catmull-Rom splines (tension = 0.5)
      const advancedPathString = createCatmullRomPath(smoothedPoints, 0.5);
      
      if (advancedPathString && advancedPathString.trim() !== '') {
        fabricObject = new fabric.Path(advancedPathString, {
          ...commonProps,
          strokeDashArray: undefined
        });
      }
      break;
    
    case 'highlighter':
    default:
      // For other drawing tools, use standard smoothing
      const pathString = getPointsToSmoothPath(points);
      
      if (pathString && pathString.trim() !== '') {
        fabricObject = new fabric.Path(pathString, {
          ...commonProps,
          strokeDashArray: undefined
        });
      }
      break;
  }

  // Apply transform if it exists
  if (fabricObject && stroke.transform) {
    fabricObject.set({
      left: stroke.transform.left,
      top: stroke.transform.top,
      scaleX: stroke.transform.scaleX,
      scaleY: stroke.transform.scaleY,
      angle: stroke.transform.angle
    });
  }

  return fabricObject;
};

// Animate stroke drawing
export const animateStrokeDrawing = (
  fabricObject: fabric.Object, 
  stroke: Stroke, 
  canvas: fabric.Canvas
) => {
  if (fabricObject.type === 'path') {
    const path = fabricObject as fabric.Path;
    const pathLength = stroke.points.length;
    const animationDuration = Math.min(500, Math.max(200, pathLength * 5));
    
    // Get the approximate path length
    const bounds = path.getBoundingRect();
    const approxLength = (bounds.width + bounds.height) * 2;
    
    // Set initial stroke dash array
    path.set({
      strokeDashArray: [0, approxLength],
      strokeDashOffset: 0
    });
    
    canvas.add(path);
    
    // Animate the dash array
    let startTime = performance.now();
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutQuad(progress);
      
      const visibleLength = approxLength * easedProgress;
      path.set({
        strokeDashArray: [visibleLength, approxLength - visibleLength]
      });
      
      canvas.requestRenderAll();
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Remove dash array for final state
        path.set({
          strokeDashArray: [],
          strokeDashOffset: 0
        });
        canvas.requestRenderAll();
      }
    };
    
    requestAnimationFrame(animate);
  } else {
    // For other shapes, fade them in
    fabricObject.set('opacity', 0);
    canvas.add(fabricObject);
    
    fabricObject.animate({ opacity: 1 }, {
      duration: 200,
      onChange: () => canvas.requestRenderAll()
    });
  }
};

// Easing function for smooth animation
const easeOutQuad = (t: number): number => {
  return t * (2 - t);
};